import { Box, Typography, Chip, alpha, useTheme } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Shared frame for the analysis tabs.
 *
 * Every module returns the same envelope from the backend — findings, a panel
 * of chart-ready arrays, metrics, or a reason it could not run — so the tabs
 * present it the same way rather than each inventing a layout.
 */
export const PanelShell = ({ title, subtitle, health, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {health != null && <HealthPill score={health} />}
      </Box>
      {children}
    </Box>
  );
};

export const HealthPill = ({ score }) => {
  const theme = useTheme();
  const tone =
    score >= 90 ? theme.palette.success.main : score >= 70 ? theme.palette.warning.main : theme.palette.error.main;
  return (
    <Chip
      label={`${score}/100`}
      size="small"
      sx={{ fontFamily: "monospace", fontWeight: 700, color: tone, backgroundColor: alpha(tone, 0.12) }}
    />
  );
};

export const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
      gap: 1.5,
      mb: 2,
    }}
  >
    {children}
  </Box>
);

export const StatTile = ({ label, value, unit, tone = "neutral", hint }) => {
  const theme = useTheme();
  const color =
    tone === "good"
      ? theme.palette.success.main
      : tone === "warn"
        ? theme.palette.warning.main
        : tone === "bad"
          ? theme.palette.error.main
          : theme.palette.text.primary;
  return (
    <Box
      title={hint}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.12 : 0.9),
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700, color, wordBreak: "break-word" }}>
        {value}
        {unit && (
          <Typography component="span" variant="body2" sx={{ ml: 0.5, color: theme.palette.text.secondary }}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

/** Shown in place of a panel when the backend could not run that module. */
export const PanelUnavailable = ({ reason }) => {
  const theme = useTheme();
  return (
    <Box sx={{ py: 5, textAlign: "center" }}>
      <InfoOutlinedIcon sx={{ fontSize: 28, color: theme.palette.text.secondary, mb: 1 }} />
      <Typography variant="body2" sx={{ mx: "auto", maxWidth: 520, color: theme.palette.text.secondary }}>
        {reason}
      </Typography>
    </Box>
  );
};

const SEVERITY_ICON = {
  critical: ErrorOutlineOutlinedIcon,
  warn: WarningAmberOutlinedIcon,
  good: CheckCircleOutlineOutlinedIcon,
  info: InfoOutlinedIcon,
};

/**
 * Findings as the backend computed them: the number that tripped the rule, the
 * physical check it implies, and any parameter change it suggests.
 */
export const FindingList = ({ findings = [], baseUs = null }) => {
  const theme = useTheme();
  if (findings.length === 0) return null;

  const toneFor = (severity) =>
    severity === "critical"
      ? theme.palette.error.main
      : severity === "warn"
        ? theme.palette.warning.main
        : severity === "good"
          ? theme.palette.success.main
          : theme.palette.info.main;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 3 }}>
      {findings.map((f) => {
        const tone = toneFor(f.severity);
        const Icon = SEVERITY_ICON[f.severity] || InfoOutlinedIcon;
        const at = f.at_us != null && baseUs != null ? clock(f.at_us, baseUs) : null;
        return (
          <Box
            key={f.id}
            sx={{
              display: "flex",
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(tone, 0.35)}`,
              backgroundColor: alpha(tone, 0.06),
            }}
          >
            <Icon sx={{ fontSize: 20, color: tone, mt: 0.25 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  {f.title}
                </Typography>
                <Chip
                  label={f.severity}
                  size="small"
                  sx={{ height: 20, fontSize: 11, color: tone, backgroundColor: alpha(tone, 0.14) }}
                />
                {at && (
                  <Typography variant="caption" sx={{ fontFamily: "monospace", color: theme.palette.text.secondary }}>
                    {at}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ mt: 0.75, color: theme.palette.text.primary }}>
                {f.evidence}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
                {f.hint}
              </Typography>
              {f.params?.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {f.params.map((p) => (
                    <Box
                      key={p.name}
                      sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", fontSize: 13 }}
                    >
                      <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13, color: tone }}>
                        {p.name}
                      </Typography>
                      <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {p.current != null ? `${p.current} →` : "set to"}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: theme.palette.text.primary }}
                      >
                        {p.suggested}
                        {p.unit ? ` ${p.unit}` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const clock = (us, baseUs) => {
  const total = Math.max(0, Math.round((us - baseUs) / 1e6));
  return `+${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
